import { CheckCircleOutlined, ClearOutlined, CloseOutlined, DeleteOutlined, PlusOutlined, SaveOutlined, SearchOutlined, StopOutlined } from '@ant-design/icons'
import { Avatar, Button, Divider, Drawer, Input, Modal, Select, Space, Table, Tag, Typography } from 'antd'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import api from '~/services/api'
import CanadianProvinces from '~/utils/canadianProvinces'
import CountryList from '~/utils/countriesList'
import UsStates from '~/utils/usStates'

import {
  Content,
  DrawerBody,
  FieldBlock,
  Footer,
  FormGrid,
  FullWidth,
  HeaderInfo,
  Label,
  LinkedOrderItem,
  LinkedOrderLink,
  LinkedOrderMain,
  LinkedOrdersCount,
  LinkedOrdersHeader,
  LinkedOrdersList,
  LinkedOrdersSection,
  OrderItemCard,
  ReplacementItemRow,
  ReplacementItemsHeader,
  SectionTitle,
  SelectionCard,
  SerialTag,
} from './styles'

const tagColor = {
  awaiting_shipment: {
    label: 'Awaiting Shipment',
    color: 'orange',
  },
  cancelled: {
    label: 'Cancelled',
    color: 'red',
  },
  shipped: {
    label: 'Shipped',
    color: 'green',
  },
  on_hold: {
    label: 'On Hold',
    color: 'cyan',
  },
  awaiting_payment: {
    label: 'Awaiting Payment',
    color: 'blue',
  },
  null: {
    label: 'No Status',
    color: 'magenta',
  },
}

const { Paragraph, Text, Title } = Typography
const { TextArea } = Input
const { Option } = Select

const SHIP_TO_FIELDS = ['name', 'company', 'street1', 'street2', 'street3', 'city', 'state', 'postalCode', 'country', 'phone', 'residential', 'addressVerified']
const ADDRESS_LABELS = {
  name: 'Name',
  company: 'Company',
  street1: 'Street 1',
  street2: 'Street 2',
  street3: 'Street 3',
  city: 'City',
  state: 'State',
  postalCode: 'Postal Code',
  country: 'Country',
  phone: 'Phone',
  residential: 'Residential',
  addressVerified: 'Address Verified',
}

export default function New({ open, handleClose, rma, rmaIdParam }) {
  const isEditingRma = useMemo(() => Object.keys(rma || {}).length > 0, [rma])

  const [order, setOrder] = useState('')
  const [store, setStore] = useState(undefined)
  const [searchMode, setSearchMode] = useState('orderStore')
  const [serialNumberSearch, setSerialNumberSearch] = useState('')
  const [orderCandidates, setOrderCandidates] = useState([])
  const [orderSelectionOpen, setOrderSelectionOpen] = useState(false)
  const [loadedOrder, setLoadedOrder] = useState(null)
  const [customer, setCustomer] = useState('')
  const [company, setCompany] = useState('')
  const [orderDate, setOrderDate] = useState('')
  const [items, setItems] = useState([])
  const [checklist, setChecklist] = useState([])
  const [serialReturned, setSerialReturned] = useState([])
  const [returnReason, setReturnReason] = useState(undefined)
  const [carrier, setCarrier] = useState(undefined)
  const [tracking, setTracking] = useState('')
  const [customerComments, setCustomerComments] = useState('')
  const [actionRequired, setActionRequired] = useState('')
  const [technicianLog, setTechnicianLog] = useState('')
  const [trackingStatus, setTrackingStatus] = useState(undefined)
  const [returnStatus, setReturnStatus] = useState(undefined)
  const [customerWants, setCustomerWants] = useState(undefined)
  const [closeReturnButton, setCloseReturnButton] = useState(false)
  const [overThirtyDays, setOverThirtyDays] = useState(false)
  const [storeList, setStoreList] = useState([])
  const [editingRmaId, setEditingRmaId] = useState(null)
  const [shouldReloadOnClose, setShouldReloadOnClose] = useState(false)
  const [replacementDrawerOpen, setReplacementDrawerOpen] = useState(false)
  const [creatingReplacementOrder, setCreatingReplacementOrder] = useState(false)
  const [deletingReplacementOrderId, setDeletingReplacementOrderId] = useState(null)
  const [linkedReplacementOrders, setLinkedReplacementOrders] = useState([])
  const [replacementSourceOrderId, setReplacementSourceOrderId] = useState(null)
  const [replacementForm, setReplacementForm] = useState({
    orderNumber: '',
    shipByDate: '',
    orderStatus: 'awaiting_shipment',
    customerUsername: '',
    customerEmail: '',
    shipTo: {
      name: '',
      company: '',
      street1: '',
      street2: '',
      street3: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      phone: '',
      residential: false,
      addressVerified: '',
    },
    items: [{ sku: '', name: '', quantity: 1 }],
    internalNotes: '',
  })
  const [assignDrawerOpen, setAssignDrawerOpen] = useState(false)
  const [assignOrderSearch, setAssignOrderSearch] = useState('')
  const [assignSearchResults, setAssignSearchResults] = useState([])
  const [assignSearching, setAssignSearching] = useState(false)
  const [selectedOrderToAssign, setSelectedOrderToAssign] = useState(null)
  const [assigningOrder, setAssigningOrder] = useState(false)
  const [removingAssignmentId, setRemovingAssignmentId] = useState(null)

  const isEditingCurrentRma = isEditingRma || Boolean(editingRmaId)
  const currentRmaId = editingRmaId || rma?.id
  const allCountryOptions = useMemo(() => CountryList(), [])
  const prioritizedCountryOptions = useMemo(() => {
    const priorityCodes = ['CA', 'US']
    const priority = priorityCodes.map((code) => allCountryOptions.find((country) => country.value === code)).filter(Boolean)
    const remainder = allCountryOptions.filter((country) => !priorityCodes.includes(country.value))
    return [...priority, ...remainder]
  }, [allCountryOptions])

  const refOrder = useRef(null)
  const lastSavedSerials = useRef([])

  const closeDrawer = useCallback(
    (forceReload = false) => {
      const reload = forceReload || shouldReloadOnClose
      setShouldReloadOnClose(false)
      handleClose(reload)
    },
    [handleClose, shouldReloadOnClose]
  )

  function applyOrderDetails(orderDetailsData) {
    if (!orderDetailsData) return

    setLoadedOrder(orderDetailsData)
    setOrder(orderDetailsData.order_num?.trim() || '')
    setStore(orderDetailsData.store?.id || undefined)
    setCustomer(orderDetailsData.customer?.name || '')
    setCompany(orderDetailsData.customer?.company || '')
    setOrderDate(orderDetailsData.modify_date || '')
    setItems(orderDetailsData.order_items || [])
    setChecklist(orderDetailsData.checklist || [])

    const orderDateMoment = moment(orderDetailsData.modify_date, 'YYYY-MM-DD')
    const currentDateMoment = moment(moment(), 'YYYY-MM-DD')
    setOverThirtyDays(currentDateMoment.diff(orderDateMoment, 'days') > 30)
  }

  function normalizeLinkedReplacementOrders(orders) {
    if (!orders) return []

    let parsedOrders = orders
    if (typeof orders === 'string') {
      try {
        parsedOrders = JSON.parse(orders)
      } catch (error) {
        parsedOrders = []
      }
    }

    if (!Array.isArray(parsedOrders)) return []

    return parsedOrders
      .map((replacement) => ({
        id: replacement?.id,
        replacementOrderNumber: replacement?.replacementOrderNumber,
        replacementOrderId: replacement?.replacementOrderId,
        replacementOrderStatus: replacement?.replacementOrderStatus,
        sourceOrderId: replacement?.sourceOrderId,
        sourceOrderNumber: replacement?.sourceOrderNumber,
      }))
      .filter((orderData) => orderData.replacementOrderNumber)
  }

  function mapCustomerToAddress(customerData = {}) {
    const rawCountry = customerData.country || ''
    const normalizedCountry =
      typeof rawCountry === 'string' && rawCountry.length === 2
        ? rawCountry.toUpperCase()
        : allCountryOptions.find((country) => country.label.toLowerCase() === String(rawCountry).toLowerCase())?.value || ''

    return {
      name: customerData.name || '',
      company: customerData.company || '',
      street1: customerData.street1 || '',
      street2: customerData.street2 || '',
      street3: customerData.street3 || '',
      city: customerData.city || '',
      state: customerData.state || '',
      postalCode: customerData.postalCode || customerData.postal_code || '',
      country: normalizedCountry,
      phone: customerData.phone || '',
      residential: Boolean(customerData.residential),
      addressVerified: customerData.addressVerified || '',
    }
  }

  const handleReplacementFieldChange = (fieldName, value) => {
    setReplacementForm((prev) => ({
      ...prev,
      [fieldName]: value,
    }))
  }

  const handleReplacementAddressChange = (section, fieldName, value) => {
    setReplacementForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [fieldName]: value,
        ...(fieldName === 'country' ? { state: '' } : {}),
      },
    }))
  }

  const handleReplacementItemChange = (index, fieldName, value) => {
    setReplacementForm((prev) => {
      const nextItems = [...prev.items]
      nextItems[index] = {
        ...nextItems[index],
        [fieldName]: value,
      }

      return {
        ...prev,
        items: nextItems,
      }
    })
  }

  const handleAddReplacementItem = () => {
    setReplacementForm((prev) => ({
      ...prev,
      items: [...prev.items, { sku: '', name: '', quantity: 1 }],
    }))
  }

  const handleRemoveReplacementItem = (index) => {
    setReplacementForm((prev) => {
      if (prev.items.length === 1) {
        return prev
      }

      return {
        ...prev,
        items: prev.items.filter((_, itemIndex) => itemIndex !== index),
      }
    })
  }

  const getStateOptions = (countryCode) => {
    if (countryCode === 'US') return UsStates()
    if (countryCode === 'CA') return CanadianProvinces()
    return []
  }

  const renderAddressField = (section, fieldKey) => {
    const sectionData = replacementForm[section]
    const countryCode = sectionData.country || ''

    if (fieldKey === 'residential') {
      return <Input value={sectionData.residential ? 'Yes' : 'No'} disabled />
    }

    if (fieldKey === 'addressVerified') {
      return <Input value={sectionData[fieldKey]} disabled />
    }

    if (fieldKey === 'country') {
      return (
        <Select
          value={sectionData.country}
          showSearch
          optionFilterProp="children"
          onChange={(value) => handleReplacementAddressChange(section, 'country', value)}
        >
          {prioritizedCountryOptions.map((country) => (
            <Option key={`${section}-country-${country.value}`} value={country.value}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <img
                  src={`https://flagcdn.com/w20/${country.value.toLowerCase()}.png`}
                  srcSet={`https://flagcdn.com/w40/${country.value.toLowerCase()}.png 2x`}
                  width="20"
                  alt={country.label}
                />
                <span>{country.label}</span>
              </span>
            </Option>
          ))}
        </Select>
      )
    }

    if (fieldKey === 'state') {
      const stateOptions = getStateOptions(countryCode)
      if (stateOptions.length > 0) {
        return (
          <Select
            value={sectionData.state}
            showSearch
            optionFilterProp="children"
            onChange={(value) => handleReplacementAddressChange(section, 'state', value)}
          >
            {stateOptions.map((stateOption) => (
              <Option key={`${section}-state-${stateOption.value}`} value={stateOption.value}>
                {stateOption.label}
              </Option>
            ))}
          </Select>
        )
      }
    }

    return <Input value={sectionData[fieldKey]} onChange={(e) => handleReplacementAddressChange(section, fieldKey, e.target.value)} />
  }

  const openReplacementOrderDrawer = async () => {
    if (!currentRmaId) {
      toast.error('Save the RMA first to create a replacement order.')
      return
    }

    const sourceOrderId = loadedOrder?.id || rma?.order_id || rma?.order?.id
    if (!sourceOrderId) {
      toast.error('Original order information is missing. Please close and re-open this RMA.')
      return
    }

    try {
      const orderData = await api.get(`orders/${sourceOrderId}`)
      const originalOrder = orderData.data || {}
      const customerData = originalOrder.customer || {}
      const mappedAddress = mapCustomerToAddress(customerData)
      const returnedItemsMap = {}
      const serialsToMap = serialReturned || []
      serialsToMap.forEach((serialNumber) => {
        const matchedUnit = (checklist || []).find((unit) => unit.serial_number === serialNumber)
        const sku = matchedUnit?.item?.sku || matchedUnit?.item?.part_number || ''
        const name = matchedUnit?.item?.name || ''
        const key = `${sku}::${name}`

        if (!returnedItemsMap[key]) {
          returnedItemsMap[key] = {
            sku,
            name,
            quantity: 0,
          }
        }

        returnedItemsMap[key].quantity += 1
      })

      const defaultItemsFromChecklist = Object.values(returnedItemsMap).filter((item) => item.sku || item.name)

      setReplacementSourceOrderId(sourceOrderId)
      setReplacementForm({
        orderNumber: (() => {
          const baseOrder = originalOrder.order_num || `Unknown-${sourceOrderId}`
          const dateStr = moment().format('YYYYMMDD')
          return `RO-${baseOrder}-${dateStr}`
        })(),
        shipByDate: moment().add(24, 'hours').format('YYYY-MM-DDTHH:mm'),
        orderStatus: 'awaiting_shipment',
        customerUsername: customerData.customer_username || '',
        customerEmail: customerData.email || '',
        shipTo: mappedAddress,
        items: defaultItemsFromChecklist.length > 0 ? defaultItemsFromChecklist : [{ sku: '', name: '', quantity: 1 }],
        internalNotes: '',
      })
      setReplacementDrawerOpen(true)
    } catch (error) {
      toast.error('Could not load order details for replacement order.')
    }
  }

  const handleCreateReplacementOrder = async () => {
    if (!currentRmaId) {
      toast.error('Save the RMA first to create a replacement order.')
      return
    }

    const validItems = (replacementForm.items || []).filter((item) => item.name && Number(item.quantity) > 0)
    if (validItems.length === 0) {
      toast.error('Add at least one item with name and quantity.')
      return
    }

    setCreatingReplacementOrder(true)
    try {
      const payload = {
        orderNumber: replacementForm.orderNumber,
        shipByDate: replacementForm.shipByDate,
        orderStatus: 'awaiting_shipment',
        customerUsername: replacementForm.customerUsername || null,
        customerEmail: replacementForm.customerEmail || null,
        shipTo: replacementForm.shipTo,
        items: validItems.map((item) => ({
          sku: item.sku,
          name: item.name,
          quantity: Number(item.quantity),
        })),
        internalNotes: replacementForm.internalNotes,
        sourceOrderId: replacementSourceOrderId,
        rmaId: currentRmaId,
      }

      let newReplacementOrder
      try {
        newReplacementOrder = await api.post('orders/replacement', payload)
      } catch (error) {
        toast.error(error.response?.data?.error || 'Could not create replacement order.')
        // newReplacementOrder = await api.post('orders', payload);
      }

      const responseData = newReplacementOrder.data || {}
      const createdOrder = {
        id: responseData.order_id || responseData.id,
        orderNumber: responseData.order_num || responseData.orderNumber || replacementForm.orderNumber,
      }

      const nextLinkedOrders = [...linkedReplacementOrders, createdOrder].filter(
        (orderData, index, self) => self.findIndex((entry) => entry.orderNumber === orderData.orderNumber) === index
      )

      setLinkedReplacementOrders(nextLinkedOrders)
      setShouldReloadOnClose(true)
      setReplacementDrawerOpen(false)
      toast.success(`Replacement order ${createdOrder.orderNumber} created successfully.`)
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not create replacement order.')
    } finally {
      setCreatingReplacementOrder(false)
    }
  }

  const handleDeleteReplacementOrder = (replacementOrder) => {
    if (!replacementOrder?.id) {
      toast.error('This replacement order cannot be deleted because it has no ID.')
      return
    }

    Modal.confirm({
      title: 'Delete replacement order?',
      content: `Are you sure you want to delete ${replacementOrder.replacementOrderNumber}?`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        setDeletingReplacementOrderId(replacementOrder.replacementOrderId)
        try {
          await api.delete(`/orders/replacement/${replacementOrder.replacementOrderId}`)
          setLinkedReplacementOrders((prev) => prev.filter((orderData) => orderData.replacementOrderId !== replacementOrder.replacementOrderId))
          setShouldReloadOnClose(true)
          toast.success(`Replacement order ${replacementOrder.replacementOrderNumber} deleted successfully.`)
        } catch (error) {
          toast.error(error.response?.data?.error || 'Could not delete replacement order.')
        } finally {
          setDeletingReplacementOrderId(null)
        }
      },
    })
  }

  const openAssignReplacementDrawer = () => {
    if (!currentRmaId) {
      toast.error('Save the RMA first to assign a replacement order.')
      return
    }
    setAssignOrderSearch('')
    setAssignSearchResults([])
    setSelectedOrderToAssign(null)
    setAssignDrawerOpen(true)
  }

  const handleSearchAssignOrder = async () => {
    if (!assignOrderSearch.trim()) {
      toast.error('Please enter an order number to search.')
      return
    }

    setAssignSearching(true)
    try {
      const response = await api.get('orders', {
        headers: {
          filtercriteria: 'order_num',
          filtertext: assignOrderSearch.trim(),
        },
      })

      const orders = response.data?.rows || []
      if (orders.length === 0) {
        toast.error('No orders found with this order number.')
        setAssignSearchResults([])
        return
      }

      setAssignSearchResults(orders)
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not search for orders.')
      setAssignSearchResults([])
    } finally {
      setAssignSearching(false)
    }
  }

  const handleAssignReplacementOrder = async () => {
    if (!selectedOrderToAssign) {
      toast.error('Please select an order to assign.')
      return
    }

    setAssigningOrder(true)
    try {
      const response = await api.post('returns/assignReplacementOrder', {
        return_id: currentRmaId,
        replacement_order_id: selectedOrderToAssign.id,
      })

      const assignedOrder = {
        id: response.data.id,
        replacementOrderNumber: response.data.replacement_order_num,
        replacementOrderId: response.data?.replacement_order_id,
      }

      const nextLinkedOrders = [...linkedReplacementOrders, assignedOrder].filter(
        (orderData, index, self) => self.findIndex((entry) => entry.replacementOrderNumber === orderData.replacementOrderNumber) === index
      )

      setLinkedReplacementOrders(nextLinkedOrders)
      setShouldReloadOnClose(true)
      setAssignDrawerOpen(false)
      toast.success(`Order ${response.data.replacement_order_num} assigned successfully.`)
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not assign replacement order.')
    } finally {
      setAssigningOrder(false)
    }
  }

  const handleRemoveAssignment = (replacement) => {
    if (!replacement?.id) {
      toast.error('This replacement order cannot be unassigned because it has no assignment ID.')
      return
    }

    Modal.confirm({
      title: 'Remove assignment?',
      content: `Are you sure you want to remove the assignment of ${replacement.replacementOrderNumber}?`,
      okText: 'Remove',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        setRemovingAssignmentId(replacement.id)
        try {
          await api.delete(`returns/assignReplacementOrder/${replacement.id}`)
          setLinkedReplacementOrders((prev) => prev.filter((orderData) => orderData.id !== replacement.id))
          setShouldReloadOnClose(true)
          toast.success(`Assignment of ${replacement.replacementOrderNumber} removed successfully.`)
        } catch (error) {
          toast.error(error.response?.data?.error || 'Could not remove assignment.')
        } finally {
          setRemovingAssignmentId(null)
        }
      },
    })
  }

  async function handleOrderSearch() {
    if (!order || !store) {
      toast.error('Must input Order and Store fields')
      if (refOrder.current) {
        refOrder.current.focus()
      }
      return
    }

    const orderDetails = await api.get(`orders/${encodeURIComponent(order)}/${store}`)
    if (orderDetails.data.error) {
      toast.error('Order not found. Verify if Order number and Store are correct')
      if (refOrder.current) {
        refOrder.current.focus()
      }
      return
    }
    applyOrderDetails(orderDetails.data)
  }

  async function handleOrderSearchBySerialNumber() {
    if (!serialNumberSearch?.trim()) {
      toast.error('Must input Serial Number field')
      return
    }

    const response = await api.post('orders/showBySerialNumber', {
      serialNumber: serialNumberSearch.trim(),
    })

    const ordersFound = Array.isArray(response.data) ? response.data : []

    if (ordersFound.length === 0) {
      toast.error('No orders found for this serial number')
      return
    }

    if (ordersFound.length === 1) {
      applyOrderDetails(ordersFound[0])
      return
    }

    setOrderCandidates(ordersFound)
    setOrderSelectionOpen(true)
  }

  function handleSearch() {
    if (searchMode === 'serialNumber') {
      handleOrderSearchBySerialNumber()
      return
    }

    handleOrderSearch()
  }

  const columns = [
    {
      title: 'SN',
      dataIndex: 'serial_number',
      key: 'serial_number',
    },
    {
      title: 'PN',
      dataIndex: 'part_number',
      key: 'part_number',
    },
    {
      title: 'Times Returned',
      dataIndex: 'return_counter',
      key: 'return_counter',
    },
  ]

  const info = (returnCounterArray) => {
    Modal.info({
      title: 'ALERT: this item contains components that have been previously returned by clients. Shipping this item will require tech manager approval',
      content: <Table columns={columns} dataSource={returnCounterArray} rowKey="serial_number" pagination={false} size="small" />,
      width: 800,
    })
  }

  async function handleSave(action, options = {}) {
    const { closeAfterSave = true } = options

    if (!loadedOrder && (!order || !store)) {
      toast.error('Must input Order and Store fields')
      if (refOrder.current) {
        refOrder.current.focus()
      }
      return
    }

    try {
      let orderDetailsData = loadedOrder

      if (!orderDetailsData) {
        const orderDetails = await api.get(`orders/${encodeURIComponent(order)}/${store}`)
        if (orderDetails.data.error) {
          toast.error('Order not found. Verify if Order number and Store are correct')
          if (refOrder.current) {
            refOrder.current.focus()
          }
          return
        }

        orderDetailsData = orderDetails.data
      }

      const returnDetails = {
        order_id: orderDetailsData.id,
        order_num: order?.trim(),
        items_returned: !serialReturned ? '' : serialReturned.join(),
        return_reason: returnReason,
        carrier,
        store,
        tracking,
        customer_comments: customerComments,
        action: actionRequired,
        technician_log: technicianLog,
        tracking_status: trackingStatus,
        return_status: returnStatus,
        customer_wants: customerWants,
      }

      switch (action) {
        case 'SaveUpdateReturn': {
          returnDetails.status = 'open'
          if (!order || !store) {
            toast.error('Must input Order and Store fields')
            if (refOrder.current) {
              refOrder.current.focus()
            }
            return
          }

          const targetRmaId = editingRmaId || rma?.id

          if (!targetRmaId) {
            const newRma = await api.post('/returns', returnDetails)

            if (serialReturned?.length > 0) {
              await Promise.all(
                serialReturned.map(async (serialNumber) => {
                  await api.put('inventory_items', {
                    items: [
                      {
                        serial_number: serialNumber,
                        status: 'Returning',
                        action: 'RMACreated',
                      },
                    ],
                  })
                })
              )
            }

            toast.success(`RMA ${newRma.data.id} created successfully`)
            setEditingRmaId(newRma.data.id)
            setLinkedReplacementOrders([])
            lastSavedSerials.current = [...serialReturned]
            if (closeAfterSave) {
              closeDrawer(true)
            } else {
              setShouldReloadOnClose(true)
            }
            return
          }

          const updateRma = await api.put(`/returns/${targetRmaId}`, returnDetails)

          const previousSerials = lastSavedSerials.current
          const addedSerials = serialReturned.filter((s) => !previousSerials.includes(s))
          const removedSerials = previousSerials.filter((s) => !serialReturned.includes(s))

          await Promise.all([
            ...addedSerials.map((serialNumber) =>
              api.put('inventory_items', {
                items: [{ serial_number: serialNumber, status: 'Returning', action: 'RMAUpdated' }],
              })
            ),
            ...removedSerials.map((serialNumber) =>
              api.put('inventory_items', {
                items: [{ serial_number: serialNumber, status: 'Sold', action: 'RMAUpdated', location: '' }],
              })
            ),
          ])

          lastSavedSerials.current = [...serialReturned]
          toast.success(`RMA ${updateRma.data.id} updated successfully`)
          if (closeAfterSave) {
            closeDrawer(true)
          } else {
            setShouldReloadOnClose(true)
          }
          return
        }

        case 'CancelReturn': {
          returnDetails.status = 'cancelled'
          await api.put(`/returns/${rma.id}`, returnDetails)

          if (serialReturned.length === 1 && serialReturned[0] === '') {
            toast.success(`RMA ${rma.id} cancelled successfully`)
            closeDrawer(true)
            return
          }

          await Promise.all(
            serialReturned.map(async (serialNumber) => {
              await api.put('inventory_items', {
                items: [
                  {
                    serial_number: serialNumber,
                    status: 'Sold',
                    action: 'RMACancelled',
                    location: '',
                  },
                ],
              })
            })
          )

          toast.success(`RMA ${rma.id} cancelled successfully`)
          closeDrawer(true)
          return
        }

        case 'CloseReturn': {
          returnDetails.status = 'closed'
          const closeRma = await api.put(`/returns/${rma.id}`, returnDetails)
          toast.success(`RMA ${closeRma.data.id} closed successfully`)
          info(closeRma.data.returnCounterList)
          closeDrawer(true)
          break
        }

        default:
          break
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'An error occurred while saving. Please try again.')
    }
  }

  function handleClearReturn() {
    setOrder('')
    setStore(undefined)
    setSerialNumberSearch('')
    setOrderSelectionOpen(false)
    setOrderCandidates([])
    setLoadedOrder(null)
    setCustomer('')
    setCompany('')
    setOrderDate('')
    setItems([])
    setCustomerComments('')
    setActionRequired('')
    setTechnicianLog('')
    setTrackingStatus(undefined)
    setReturnStatus(undefined)
    setCustomerWants(undefined)
    setReturnReason(undefined)
    setCarrier(undefined)
    setTracking('')
    setSerialReturned([])
    setChecklist([])
    if (refOrder.current) {
      refOrder.current.focus()
    }
  }

  function handleRemoveReturnedSerial(serialChecklist) {
    const data = serialReturned.filter((i) => i !== serialChecklist)
    setSerialReturned(data)
  }

  function handleAddReturnedSerial(serialChecklist) {
    if (typeof serialReturned === 'undefined' || !serialReturned) {
      setSerialReturned([serialChecklist])
      return
    }

    if (!serialReturned.includes(serialChecklist)) {
      setSerialReturned([...serialReturned, serialChecklist])
    }
  }

  useEffect(() => {
    setLoadedOrder(
      rma?.order?.id || rma?.order_id
        ? {
            id: rma?.order?.id || rma?.order_id,
            order_num: rma?.order_num,
            store: rma?.order?.store || { id: rma?.order?.store?.id },
            customer: { name: rma?.customer, company: rma?.company },
            modify_date: rma?.order_date,
            order_items: rma?.order_items,
            checklist: rma?.checklist,
          }
        : null
    )

    setOrder(rma?.order_num?.trim() || '')
    setStore(rma?.order?.store?.id || rma?.store || undefined)
    setCustomer(rma?.customer || '')
    setCompany(rma?.company || '')
    setOrderDate(rma?.order_date || '')
    setCustomerComments(rma?.customer_comments || '')
    setActionRequired(rma?.action || '')
    setTechnicianLog(rma?.technician_log || '')
    setItems(rma?.order_items || [])
    setChecklist(rma?.checklist ? rma.checklist.filter((el) => el.serial_number !== null) : [])
    setTrackingStatus(rma?.tracking_status || undefined)
    setReturnStatus(rma?.return_status || undefined)
    setCustomerWants(rma?.customer_wants || undefined)
    setReturnReason(rma?.return_reason || undefined)
    setCarrier(rma?.carrier || undefined)
    setTracking(rma?.tracking || '')
    const initialSerials = rma?.items_returned?.length > 0 ? rma.items_returned.filter((el) => el !== '') : []
    setSerialReturned(initialSerials)
    lastSavedSerials.current = initialSerials
    setEditingRmaId(rma?.id || null)
    setShouldReloadOnClose(false)
    setLinkedReplacementOrders(normalizeLinkedReplacementOrders(rma?.replacement_orders || rma?.replacementOrders))
    setReplacementDrawerOpen(false)

    async function loadStores() {
      const { data: storesData } = await api.get('stores')
      setStoreList(storesData)
    }
    loadStores()

    const closeButtonEnabled =
      (rma?.return_status === 'Full Refunded' || rma?.return_status === 'Partial Refunded' || rma?.return_status === 'Replaced') &&
      rma?.tracking_status === 'Received RB'
    setCloseReturnButton(!closeButtonEnabled)
  }, [open]) // eslint-disable-line

  useEffect(() => {
    const closeButtonEnabled =
      (returnStatus === 'Full Refunded' || returnStatus === 'Partial Refunded' || returnStatus === 'Replaced') && trackingStatus === 'Received RB'
    setCloseReturnButton(!closeButtonEnabled)
  }, [returnStatus, trackingStatus]) // eslint-disable-line

  return (
    <>
      {rma && (
        <Drawer
          placement="right"
          width="78vw"
          open={open}
          onClose={() => closeDrawer(false)}
          destroyOnClose={false}
          title={
            <HeaderInfo>
              <Title level={4} style={{ margin: 0 }}>
                RMA: {editingRmaId || rma.id || 'New'}
              </Title>
              {rma.id && <Text type="secondary">created at {moment(rma.createdAt).format('MMMM DD, YYYY')}</Text>}
            </HeaderInfo>
          }
        >
          <DrawerBody>
            <Content>
              <Space style={{ marginBottom: 16 }}>
                {currentRmaId && (
                  <Button type="default" icon={<PlusOutlined />} onClick={openAssignReplacementDrawer} style={{ marginTop: 8 }}>
                    Assign Replacement Order
                  </Button>
                )}
              </Space>

              {linkedReplacementOrders.length > 0 && (
                <LinkedOrdersSection>
                  <LinkedOrdersHeader>
                    <Text strong>Linked Replacement Orders</Text>
                    <LinkedOrdersCount>{linkedReplacementOrders.length}</LinkedOrdersCount>
                  </LinkedOrdersHeader>

                  <LinkedOrdersList>
                    {linkedReplacementOrders.map((replacementOrder) => (
                      <LinkedOrderItem key={`${replacementOrder.id}`}>
                        <LinkedOrderMain>
                          {replacementOrder.replacementOrderId ? (
                            <LinkedOrderLink href={`/order/${replacementOrder.replacementOrderId}`} target="_blank" rel="noopener noreferrer">
                              {replacementOrder.replacementOrderNumber}
                            </LinkedOrderLink>
                          ) : (
                            <Text>{replacementOrder.replacementOrderNumber}</Text>
                          )}
                          {replacementOrder?.replacementOrderStatus && <Text type="secondary">{replacementOrder.replacementOrderStatus}</Text>}
                        </LinkedOrderMain>

                        <div style={{ display: 'flex', gap: 8 }}>
                          {replacementOrder.id && (
                            <Button
                              size="small"
                              type="text"
                              danger
                              icon={<CloseOutlined />}
                              loading={removingAssignmentId === replacementOrder.id}
                              onClick={() => handleRemoveAssignment(replacementOrder)}
                            >
                              Remove Assignment
                            </Button>
                          )}
                          <Button
                            size="small"
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            loading={deletingReplacementOrderId === replacementOrder.id}
                            onClick={() => handleDeleteReplacementOrder(replacementOrder)}
                            disabled={!replacementOrder.id}
                          >
                            Delete
                          </Button>
                        </div>
                      </LinkedOrderItem>
                    ))}
                  </LinkedOrdersList>
                </LinkedOrdersSection>
              )}

              <FormGrid>
                <FieldBlock>
                  <Label>Order #</Label>
                  <Input
                    id="order_number"
                    disabled={isEditingCurrentRma || searchMode === 'serialNumber'}
                    ref={refOrder}
                    value={order}
                    onChange={(e) => {
                      setOrder(e.target.value)
                      setLoadedOrder(null)
                    }}
                  />
                </FieldBlock>

                <FieldBlock>
                  <Label>Search mode</Label>
                  <Select
                    disabled={isEditingCurrentRma}
                    value={searchMode}
                    onChange={(value) => {
                      setSearchMode(value)
                      setLoadedOrder(null)
                      setOrder('')
                      setStore(undefined)
                      setSerialNumberSearch('')
                    }}
                  >
                    <Option value="orderStore">Order # + Store</Option>
                    <Option value="serialNumber">Serial Number</Option>
                  </Select>
                </FieldBlock>

                {searchMode === 'serialNumber' && (
                  <FieldBlock>
                    <Label>Serial Number</Label>
                    <Input
                      id="serial_number_search"
                      disabled={isEditingCurrentRma}
                      value={serialNumberSearch}
                      onChange={(e) => {
                        setSerialNumberSearch(e.target.value)
                        setLoadedOrder(null)
                      }}
                    />
                  </FieldBlock>
                )}

                <FieldBlock>
                  <Label>Store</Label>
                  <Select
                    disabled={isEditingCurrentRma || searchMode === 'serialNumber'}
                    value={store}
                    onChange={(value) => {
                      setStore(value)
                      setLoadedOrder(null)
                    }}
                    placeholder="Select a store"
                  >
                    {storeList.length > 0 &&
                      storeList.map((el) => (
                        <Option key={el.id} value={el.id}>
                          {el.name}
                        </Option>
                      ))}
                  </Select>
                </FieldBlock>

                <FieldBlock>
                  <Label>&nbsp;</Label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button disabled={isEditingCurrentRma} type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                      Search
                    </Button>
                    <Button disabled={isEditingCurrentRma} icon={<ClearOutlined />} onClick={handleClearReturn}>
                      Clear
                    </Button>
                  </div>
                </FieldBlock>
              </FormGrid>

              <Divider />

              <FormGrid>
                <FieldBlock>
                  <Label>Store</Label>
                  <Input disabled value={store || ''} />
                </FieldBlock>

                <FieldBlock>
                  <Label>Customer</Label>
                  <Input disabled value={company ? `${customer} (${company})` : customer || ''} />
                </FieldBlock>

                <FieldBlock>
                  <Label>Order Date {overThirtyDays ? '(Over 30 days)' : ''}</Label>
                  <Input disabled status={overThirtyDays ? 'error' : ''} value={orderDate ? moment(orderDate).format('MMMM DD, YYYY') : ''} />
                </FieldBlock>

                <FieldBlock>
                  <Label>Return reason</Label>
                  <Select value={returnReason} onChange={setReturnReason} allowClear>
                    <Option value="Claim damage">Claim damage</Option>
                    <Option value="Defective">Defective</Option>
                    <Option value="Delivery issue">Delivery issue</Option>
                    <Option value="Different/Wrong Item">Different/Wrong Item</Option>
                    <Option value="No longer needed">No longer needed</Option>
                    <Option value="No reason given">No reason given</Option>
                    <Option value="Overstock">Overstock</Option>
                    <Option value="Product dissatisfaction">Product dissatisfaction</Option>
                    <Option value="Repair service">Repair service</Option>
                    <Option value="Wrong description">Wrong description</Option>
                  </Select>
                </FieldBlock>

                <FieldBlock>
                  <Label>Carrier</Label>
                  <Select value={carrier} onChange={setCarrier} allowClear>
                    <Option value="Amazon">Amazon</Option>
                    <Option value="Canadapost">Canadapost</Option>
                    <Option value="DHL">DHL</Option>
                    <Option value="Fedex">Fedex</Option>
                    <Option value="Purolator">Purolator</Option>
                    <Option value="UPS">UPS</Option>
                    <Option value="USPS">USPS</Option>
                    <Option value="Other">Other</Option>
                  </Select>
                </FieldBlock>

                <FieldBlock>
                  <Label>Tracking Code</Label>
                  <Input value={tracking} onChange={(e) => setTracking(e.target.value)} />
                </FieldBlock>

                <FieldBlock>
                  <Label>Customer wants</Label>
                  <Select value={customerWants} onChange={setCustomerWants} allowClear>
                    <Option value="Full Refund">Full Refund</Option>
                    <Option value="Partial Refund">Partial Refund</Option>
                    <Option value="Replacement">Replacement</Option>
                  </Select>
                </FieldBlock>

                <FieldBlock>
                  <Label>Return Status</Label>
                  <Select value={returnStatus} onChange={setReturnStatus} allowClear>
                    <Option value="Pending Full Refund">Pending Full Refund</Option>
                    <Option value="Pending Partial Refund">Pending Partial Refund</Option>
                    <Option value="Pending Replacement">Pending Replacement</Option>
                    <Option value="Full Refunded">Full Refunded</Option>
                    <Option value="Partial Refunded">Partial Refunded</Option>
                    <Option value="Replaced">Replaced</Option>
                  </Select>
                </FieldBlock>

                <FieldBlock>
                  <Label>Tracking Status</Label>
                  <Select value={trackingStatus} onChange={setTrackingStatus} allowClear>
                    <Option value="Returning">Returning</Option>
                    <Option value="Received CBI">Received CBI</Option>
                    <Option value="Received RB">Received RB</Option>
                  </Select>
                </FieldBlock>

                <FullWidth>
                  <FieldBlock>
                    <Label>Customer comments</Label>
                    <TextArea value={customerComments} onChange={(e) => setCustomerComments(e.target.value)} autoSize={{ minRows: 3 }} />
                  </FieldBlock>
                </FullWidth>

                <FullWidth>
                  <FieldBlock>
                    <Label>Action required</Label>
                    <TextArea value={actionRequired} onChange={(e) => setActionRequired(e.target.value)} autoSize={{ minRows: 3 }} />
                  </FieldBlock>
                </FullWidth>

                {/* <FullWidth>
                  <FieldBlock>
                    <Label>Technician log</Label>
                    <TextArea disabled value={technicianLog} onChange={(e) => setTechnicianLog(e.target.value)} autoSize={{ minRows: 3 }} />
                  </FieldBlock>
                </FullWidth> */}
              </FormGrid>

              {store && <SectionTitle level={5}>Items as ordered</SectionTitle>}
              {items?.map((item) => (
                <OrderItemCard key={item.serial_number || item.title}>
                  <Avatar>{item.quantity}</Avatar>
                  <Text>{item.title}</Text>
                </OrderItemCard>
              ))}

              {store && <SectionTitle level={5}>Items shipped</SectionTitle>}
              {checklist?.map((unit) => (
                <Paragraph key={unit.serial_number} copyable={{ text: unit.serial_number }}>
                  <SerialTag color="blue" onClick={() => handleAddReturnedSerial(unit.serial_number)}>
                    {unit.serial_number} - {unit.item?.name || unit.item}
                  </SerialTag>
                </Paragraph>
              ))}

              <SectionTitle level={5}>Items being returned</SectionTitle>
              {serialReturned?.map((serial) => {
                const unit = checklist && checklist.find((u) => u.serial_number === serial)
                const label = unit && (unit.item?.name || unit.item) ? `${serial} - ${unit.item?.name || unit.item}` : serial

                return (
                  <Paragraph key={serial} copyable={{ text: serial }}>
                    <SerialTag color="red" onClick={() => handleRemoveReturnedSerial(serial)}>
                      {label}
                    </SerialTag>
                  </Paragraph>
                )
              })}
            </Content>

            <Footer>
              {!rmaIdParam && (
                <Button disabled={rma.status === 'closed'} icon={<StopOutlined />} onClick={() => handleSave('CancelReturn')}>
                  Cancel Return
                </Button>
              )}

              {!rmaIdParam && (
                <Button icon={<CheckCircleOutlined />} onClick={() => handleSave('CloseReturn')} disabled={closeReturnButton || rma.status === 'closed'}>
                  Complete Return
                </Button>
              )}

              {!rmaIdParam && (
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={() => handleSave('SaveUpdateReturn', { closeAfterSave: false })}
                  disabled={rma.status === 'closed'}
                >
                  Save
                </Button>
              )}

              {!rmaIdParam && (
                <Button
                  type="primary"
                  ghost
                  icon={<SaveOutlined />}
                  onClick={() => handleSave('SaveUpdateReturn', { closeAfterSave: true })}
                  disabled={rma.status === 'closed'}
                >
                  Save and Close
                </Button>
              )}

              <Button type="default" icon={<PlusOutlined />} onClick={openReplacementOrderDrawer}>
                Create Replacement Order
              </Button>

              <Button danger icon={<CloseOutlined />} onClick={() => closeDrawer(false)}>
                Close
              </Button>
            </Footer>
          </DrawerBody>
        </Drawer>
      )}

      <Modal
        title="Select the Order"
        open={orderSelectionOpen}
        onCancel={() => setOrderSelectionOpen(false)}
        width={900}
        footer={[
          <Button key="cancel" onClick={() => setOrderSelectionOpen(false)}>
            Cancel
          </Button>,
        ]}
      >
        <Text>Multiple orders were found for this serial number. Please select the correct one.</Text>
        <div style={{ marginTop: 12 }}>
          {orderCandidates.map((candidate) => (
            <SelectionCard key={candidate.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                <div>
                  <div>
                    <Text>
                      Order #: {candidate.order_num} | Store: {candidate.store?.name || candidate.store?.id || '-'} | Customer:{' '}
                      {candidate.customer?.name || '-'}
                    </Text>
                  </div>
                  <Text type="secondary">Order Date: {moment(candidate.modify_date).format('MMMM DD, YYYY')}</Text>
                </div>
                <Button
                  type="primary"
                  onClick={() => {
                    applyOrderDetails(candidate)
                    setOrderSelectionOpen(false)
                  }}
                >
                  Select
                </Button>
              </div>
            </SelectionCard>
          ))}
        </div>
      </Modal>

      <Drawer title="Assign Replacement Order" placement="right" width="60vw" open={assignDrawerOpen} onClose={() => setAssignDrawerOpen(false)}>
        <FormGrid>
          <FieldBlock>
            <Label>Order Number</Label>
            <Input
              value={assignOrderSearch}
              onChange={(e) => setAssignOrderSearch(e.target.value)}
              placeholder="Enter order number"
              onPressEnter={handleSearchAssignOrder}
            />
          </FieldBlock>
          <FieldBlock>
            <Label>&nbsp;</Label>
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearchAssignOrder} loading={assignSearching}>
              Search
            </Button>
          </FieldBlock>
        </FormGrid>

        {assignSearchResults.length > 0 && (
          <>
            <SectionTitle level={5}>Search Results ({assignSearchResults.length})</SectionTitle>
            <div style={{ marginTop: 12 }}>
              {assignSearchResults.map((orderResult) => (
                <SelectionCard
                  key={orderResult.id}
                  style={{
                    border: selectedOrderToAssign?.id === orderResult.id ? '2px solid #1890ff' : undefined,
                    cursor: 'pointer',
                  }}
                  onClick={() => setSelectedOrderToAssign(orderResult)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text strong>Order #: {orderResult.order_num}</Text>
                        <Tag color={tagColor[orderResult.status]?.color} key={tagColor[orderResult.status]?.label}>
                          {tagColor[orderResult.status]?.label?.toUpperCase() || orderResult.status}
                        </Tag>
                      </div>
                      <div>
                        <Text type="secondary">Date: {moment(orderResult.order_date).format('MMMM DD, YYYY')}</Text>
                      </div>
                      <div>
                        <Text type="secondary">Customer: {orderResult.customer?.name || '-'}</Text>
                      </div>
                      <div>
                        <Text type="secondary">Store: {orderResult.store?.name || '-'}</Text>
                      </div>
                      <div>
                        <Text type="secondary">Status: {orderResult.status || '-'}</Text>
                      </div>
                      {orderResult.order_items?.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <Text type="secondary">Items:</Text>
                          {orderResult.order_items.map((item) => (
                            <div key={item.id} style={{ marginLeft: 12 }}>
                              <Text type="secondary">
                                • {item.title} (Qty: {item.quantity})
                              </Text>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {selectedOrderToAssign?.id === orderResult.id && <CheckCircleOutlined style={{ fontSize: 24, color: '#1890ff' }} />}
                  </div>
                </SelectionCard>
              ))}
            </div>
          </>
        )}

        <Footer>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={handleAssignReplacementOrder}
            loading={assigningOrder}
            disabled={!selectedOrderToAssign}
          >
            Assign Order
          </Button>
          <Button onClick={() => setAssignDrawerOpen(false)}>Close</Button>
        </Footer>
      </Drawer>

      <Drawer title="Create Replacement Order" placement="right" width="60vw" open={replacementDrawerOpen} onClose={() => setReplacementDrawerOpen(false)}>
        <FormGrid>
          <FieldBlock>
            <Label>Order Number</Label>
            <Input value={replacementForm.orderNumber} onChange={(e) => handleReplacementFieldChange('orderNumber', e.target.value)} />
          </FieldBlock>

          <FieldBlock>
            <Label>Ship By Date</Label>
            <Input type="datetime-local" value={replacementForm.shipByDate} onChange={(e) => handleReplacementFieldChange('shipByDate', e.target.value)} />
          </FieldBlock>

          <FieldBlock>
            <Label>Order Status</Label>
            <Input value="Awaiting Shipment" disabled />
          </FieldBlock>

          <FieldBlock>
            <Label>Customer Username</Label>
            <Input value={replacementForm.customerUsername} onChange={(e) => handleReplacementFieldChange('customerUsername', e.target.value)} />
          </FieldBlock>

          <FieldBlock>
            <Label>Customer Email</Label>
            <Input value={replacementForm.customerEmail} onChange={(e) => handleReplacementFieldChange('customerEmail', e.target.value)} />
          </FieldBlock>
        </FormGrid>

        <SectionTitle level={5}>Ship To</SectionTitle>
        <FormGrid>
          {SHIP_TO_FIELDS.map((fieldKey) => (
            <FieldBlock key={`shipTo-${fieldKey}`}>
              <Label style={{ display: 'block', width: '100%' }}>{ADDRESS_LABELS[fieldKey] || fieldKey}</Label>
              {renderAddressField('shipTo', fieldKey)}
            </FieldBlock>
          ))}
        </FormGrid>

        <SectionTitle level={5}>Items</SectionTitle>
        <ReplacementItemsHeader>
          <Text>Add one or multiple items</Text>
          <Button icon={<PlusOutlined />} onClick={handleAddReplacementItem}>
            Add Item
          </Button>
        </ReplacementItemsHeader>

        {replacementForm.items.map((item, index) => (
          <ReplacementItemRow key={`replacement-item-${Math.random()}`}>
            <Input placeholder="SKU" value={item.sku} onChange={(e) => handleReplacementItemChange(index, 'sku', e.target.value)} />
            <Input placeholder="Name" value={item.name} onChange={(e) => handleReplacementItemChange(index, 'name', e.target.value)} />
            <Input
              type="number"
              min={1}
              placeholder="Quantity"
              value={item.quantity}
              onChange={(e) => handleReplacementItemChange(index, 'quantity', e.target.value)}
            />
            <Button danger icon={<DeleteOutlined />} onClick={() => handleRemoveReplacementItem(index)} disabled={replacementForm.items.length === 1}>
              Remove
            </Button>
          </ReplacementItemRow>
        ))}

        <SectionTitle level={5}>Notes</SectionTitle>
        <FormGrid>
          <FullWidth>
            <FieldBlock>
              <Label>ShipStation Internal Notes</Label>
              <TextArea
                value={replacementForm.internalNotes}
                onChange={(e) => handleReplacementFieldChange('internalNotes', e.target.value)}
                autoSize={{ minRows: 3 }}
              />
            </FieldBlock>
          </FullWidth>
        </FormGrid>

        <Footer>
          <Button type="primary" icon={<PlusOutlined />} loading={creatingReplacementOrder} onClick={handleCreateReplacementOrder}>
            Create Replacement Order
          </Button>
          <Button onClick={() => setReplacementDrawerOpen(false)}>Close</Button>
        </Footer>
      </Drawer>
    </>
  )
}
